import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, useLang, formatCurrency } from "../lib/providers";
import { PageHeader, Btn, StatusBadge, EmptyState } from "../components/primitives";
import BulkActionBar, { Checkbox } from "../components/BulkActionBar";
import { useConfirm } from "../components/ConfirmDialog";
import { ArrowLeft, ArrowCounterClockwise, Trash } from "@phosphor-icons/react";

export default function InvoiceArchivePage() {
  const { t, lang } = useLang();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  const load = () => {
    setLoading(true);
    api.get("/invoices/archive").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const restoreOne = async (id) => {
    await api.post(`/invoices/${id}/restore`);
    load();
  };
  const hardDel = async (id, number) => {
    const ok = await confirm({
      title: `${t("permanentDelete")}: ${number}`,
      description: t("confirmHardDeleteDesc"),
      confirmText: t("permanentDelete"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/invoices/${id}/hard`);
    load();
  };
  const bulkRestore = async () => {
    await api.post("/invoices/bulk-restore", { ids: [...selected] });
    setSelected(new Set()); load();
  };
  const bulkHardDel = async () => {
    const ids = [...selected];
    const ok = await confirm({
      title: t("permanentDelete"),
      description: t("confirmDeleteMany").replace("{n}", ids.length) + " " + t("confirmHardDeleteDesc"),
      confirmText: t("permanentDelete"),
      variant: "danger",
    });
    if (!ok) return;
    for (const id of ids) await api.delete(`/invoices/${id}/hard`);
    setSelected(new Set()); load();
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <Link to="/invoices" className="inline-flex items-center gap-1.5 text-sm text-[var(--muted)] hover:text-[var(--ink)]" data-testid="back-to-invoices">
          <ArrowLeft size={14} /> {t("invoices")}
        </Link>
      </div>

      <PageHeader
        eyebrow={t("archived")}
        title={t("archive")}
        description={lang === "de"
          ? "Archivierte Rechnungen werden 30 Tage aufbewahrt und können wiederhergestellt werden."
          : "Archived invoices are retained for 30 days and can be restored."}
      />

      <BulkActionBar
        count={selected.size}
        lang={lang}
        onClear={() => setSelected(new Set())}
        onRestore={bulkRestore}
        onDelete={bulkHardDel}
        deleteLabel={t("permanentDelete")}
      />

      {loading ? (
        <div className="swiss-card p-8 text-[var(--muted)]">…</div>
      ) : items.length === 0 ? (
        <EmptyState title={t("emptyArchive")} />
      ) : (
        <div className="swiss-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg)] text-[var(--muted)]">
              <tr>
                <th className="w-8 pl-5"></th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("number")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("title")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("customer")}</th>
                <th className="text-left font-medium px-5 py-3 label-overline">{t("status")}</th>
                <th className="text-right font-medium px-5 py-3 label-overline">{t("total")}</th>
                <th className="text-right font-medium px-5 py-3 label-overline">{t("daysRemaining")}</th>
                <th className="text-right font-medium px-5 py-3 label-overline">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((x) => {
                const days = x.days_remaining ?? 30;
                const urgent = days <= 3;
                return (
                  <tr key={x.id} className={`border-t border-[var(--border)] transition-colors fade-up ${selected.has(x.id) ? "bg-[var(--brand-soft)]" : "hover:bg-[var(--row-hover)]"}`}>
                    <td className="pl-5">
                      <Checkbox checked={selected.has(x.id)} onChange={() => toggleSel(x.id)} testId={`sel-arch-${x.id}`} />
                    </td>
                    <td className="px-5 py-3 font-mono">{x.number}</td>
                    <td className="px-5 py-3 font-medium">{x.title || "—"}</td>
                    <td className="px-5 py-3 text-[var(--muted)]">{x.customer_snapshot?.name || "—"}</td>
                    <td className="px-5 py-3"><StatusBadge status={x.status} lang={lang} /></td>
                    <td className="px-5 py-3 text-right font-medium">{formatCurrency(x.total, lang)}</td>
                    <td className={`px-5 py-3 text-right font-mono ${urgent ? "text-[#FF3B30]" : "text-[var(--muted)]"}`}>
                      {days} {lang === "de" ? "Tage" : "days"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Btn variant="ghost" onClick={() => restoreOne(x.id)} data-testid={`restore-invoice-${x.id}`} className="h-8 px-2 text-[var(--brand)]">
                          <ArrowCounterClockwise size={14} />
                        </Btn>
                        <Btn variant="ghost" onClick={() => hardDel(x.id, x.number)} data-testid={`hard-del-invoice-${x.id}`} className="h-8 px-2 text-[#FF3B30] hover:bg-[#FF3B30]/5">
                          <Trash size={14} />
                        </Btn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
